import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minimize2,
  Minus,
  Monitor,
  MoveHorizontal,
  PersonStanding,
  Plus,
  Sparkles,
  TrendingUp,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/body-lift-surgery-mumbai";

const BODY_LIFT_FAQS = [
  {
    question: "What is the difference between a body lift and a tummy tuck?",
    answer:
      "A tummy tuck addresses the front abdomen only: skin removal, muscle repair, and belly button repositioning. A body lift addresses the entire circumference of the lower trunk including the flanks, lower back, buttocks, and outer thighs alongside the abdomen. Patients with skin excess confined to the front abdomen need a tummy tuck. Patients whose skin excess wraps around to the flanks, back, and buttocks need a body lift. Using a tummy tuck on the second group leaves the circumferential problem partially untreated.",
  },
  {
    question: "Can body lift be performed in a single surgery?",
    answer:
      "For some patients with moderate extent of skin excess and good medical fitness, a lower body lift can be completed in a single procedure of 4 to 8 hours. For patients with more extensive excess requiring upper and lower body lift, or for patients whose medical status does not support a very long anesthetic, staging across two or more procedures is the safer approach. Dr. Jain plans the staging individually based on the extent of correction needed and what the patient's health safely allows per session.",
  },
  {
    question: "How long does body lift recovery take?",
    answer:
      "Most patients return to desk work within 3 to 4 weeks. Full activity including exercise resumes from 6 to 8 weeks. Final body contour is visible at 3 to 6 months as deep swelling fully resolves. The scar matures over 12 to 18 months. This is a longer recovery than most other body contouring procedures and patients should plan accordingly.",
  },
  {
    question: "Will body lift result in a visible scar?",
    answer:
      "Yes. The circumferential incision produces a scar that runs around the entire waist. Dr. Jain positions this scar within the underwear and swimsuit line so it is not visible in clothing or swimwear. The scar is initially pink and raised, then flattens and fades over 12 to 18 months. For most patients, the trade-off between a concealed scar and the elimination of extensive hanging skin is straightforward. The scar is not visible in daily life. The skin excess was.",
  },
  {
    question: "Is body lift only for patients after bariatric surgery?",
    answer:
      "No. Body lift is appropriate for any patient with significant circumferential skin excess regardless of how that weight was lost. Patients who achieved major weight loss through diet and exercise are equally appropriate candidates as those who underwent bariatric surgery. The indication is the skin excess, not the method by which the weight was lost.",
  },
  {
    question: "Does body lift include a buttock lift?",
    answer:
      "Yes. Lifting the buttock is an integral component of the lower body lift. The circumferential approach elevates the lateral and posterior skin, which lifts the outer thigh and buttock as part of the same procedure. The buttock lift component uses internal tissue anchoring to the deep fascia rather than skin tension alone, which produces a more durable lift.",
  },
  {
    question: "What are the risks of body lift surgery?",
    answer:
      "Seroma is the most common complication and is managed with drain placement and compression. Wound healing delays, particularly at areas of high tension in the incision, are the second most common concern. Infection, bleeding, asymmetry, and deep vein thrombosis are possible. The extended surgical duration increases anesthetic risk compared to shorter procedures. All risks are reduced by proper patient selection, staged planning when needed, and smoking cessation before surgery.",
  },
  {
    question: "When should I consider a body lift after weight loss surgery?",
    answer:
      "After reaching a stable weight maintained for at least 3 to 6 months and after nutritional recovery from bariatric surgery is complete. Operating during active weight loss produces a result that is undermined by continued fat loss after surgery. Most bariatric patients reach their stable weight 12 to 24 months after their weight loss procedure. Body lift consultation is appropriate once this plateau has been reached and sustained.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Body lift",
      alternateName: ["Lower body lift", "Belt lipectomy", "Circumferential body lift"],
      description:
        "Circumferential excision of excess skin and fat of the lower trunk after massive weight loss; addresses abdomen, flanks, lower back, buttocks, and outer thighs; may be staged.",
      procedureType: "surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Lower trunk" },
      howPerformed:
        "Marking standing; circumferential waist incision within underwear line; tissue elevation; liposuction as needed; rectus plication if indicated; buttock anchoring to deep fascia; layered closure; drains; general anesthesia; 4–8 hours; hospital stay 1–3 nights.",
      url: PAGE_URL,
      additionalProperty: [
        {
          "@type": "PropertyValue",
          name: "Primary indication",
          value: "Post–massive weight loss circumferential skin excess",
        },
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical body contouring" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BODY_LIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function BodyLiftJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Body Lift", href: "/body-lift-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Body Lift Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Lower Body Lift &amp; Belt Lipectomy by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Body Lift" icon="✨" size="hero"  imageSrc={HERO.bodyLift} imageAlt="Body lift"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is Body Lift Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A body lift, also called a lower body lift or belt lipectomy, is a comprehensive surgical procedure that removes excess
              loose skin and fat from multiple areas of the body simultaneously through a circumferential incision around the waist. It
              addresses the abdomen, flanks, lower back, buttocks, and outer thighs in a single procedure, producing a completely
              transformed lower body contour.
            </p>
            <p>
              Body lift is the defining procedure for patients who have achieved significant weight loss and are left with skin that
              will not retract. Weight loss reduces fat volume. It does not remove the stretched skin that previously contained that fat.
              For patients who have lost 30, 50, or 100 kilograms or more, the resulting skin excess is extensive, affects multiple areas
              simultaneously, causes chronic hygiene problems and skin irritation in the folds, and cannot be addressed through any
              combination of non-surgical treatments. A body lift is the surgical answer to this specific problem.
            </p>
            <p>
              Dr. Vicky Jain performs body lift surgery for post-bariatric and post-weight loss patients who have reached a
              stable weight and are medically fit for an extended surgical procedure. The surgery is frequently staged across multiple
              sessions to maintain patient safety. The sequence and extent of each stage are planned individually based on which areas
              cause the most functional and aesthetic concern and what the patient&apos;s health safely allows in each operation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Body Lift Surgery</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Body lift surgery is among the most complex body contouring procedures performed in plastic surgery. The
                circumferential incision, the extent of tissue mobilization, the surgical duration, and the post-operative monitoring
                requirements all demand careful patient selection and meticulous surgical planning. Dr. Jain approaches every body lift as
                a staged plan rather than a single-session transformation. What can be safely achieved in one surgery, what is staged to
                a second, and the interval between stages are determined by the patient&apos;s medical status, the extent of skin excess
                in each area, and anesthesia time limits that protect patient safety.
              </p>
              <p>
                The buttock lift component of a lower body lift is particularly technique-sensitive. The buttock is lifted by anchoring
                the elevated skin and soft tissue to the deep fascia of the lower back rather than relying on skin tension alone. This
                internal support prevents the recurrence of ptosis that occurs when the lift is maintained purely by skin closure under
                tension. The incision sits within the underwear and swimsuit line so the circumferential scar is not visible in normal
                clothing.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Circumferential Abdominal Skin Excess",
      icon: Layers,
      copy: "Skin that hangs from the abdomen, flanks, and lower back simultaneously after massive weight loss. Unlike a standard tummy tuck which addresses the front abdomen only, a body lift removes skin excess all the way around the circumference of the lower trunk.",
    },
    {
      title: "Sagging Buttocks",
      icon: TrendingUp,
      copy: "Buttocks that have lost volume and descended with weight loss, producing a flat, ptotic appearance. The body lift elevates and reshapes the buttock through repositioning of the overlying soft tissue rather than implants or fat grafting.",
    },
    {
      title: "Outer Thigh Excess",
      icon: MoveHorizontal,
      copy: "Skin laxity on the outer thigh and upper leg that cannot be addressed through a standalone thigh lift without the benefit of the circumferential body lift tension. The lower body lift improves outer thigh contour as part of the circumferential approach.",
    },
    {
      title: "Skin Fold Hygiene Problems",
      icon: Zap,
      copy: "Chronic skin fold infections, rashes, and breakdown in the skin folds created by hanging abdominal, flank, and back skin. This is a functional medical problem that significantly affects quality of life and is one of the most compelling clinical indications for body lift surgery.",
    },
    {
      title: "Lower Back Rolls",
      icon: Minimize2,
      copy: "Excess skin and fat along the lower back that creates rolls above the waistband. The circumferential approach addresses back skin simultaneously with the front, which a tummy tuck alone cannot do.",
    },
    {
      title: "Complete Absence of Waistline Definition",
      icon: Sparkles,
      copy: "Patients after massive weight loss frequently have no visible waistline because circumferential skin excess obscures the underlying anatomical waist. Body lift removes this skin to reveal the waist definition that weight loss created beneath it.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Body Lift Surgery Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Lower Body Lift (Belt Lipectomy)",
    body:
      "The most commonly performed body lift. A circumferential incision is made around the entire waist, within the underwear and swimsuit line. Excess skin and fat are removed from the abdomen, flanks, lower back, buttocks, and outer thighs in a single procedure. The remaining skin is lifted upward and anchored. This is the foundational body lift procedure and what most patients mean when they say \"body lift.\" Surgery takes 4 to 8 hours and is performed under general anesthesia.",
  },
  {
    title: "Upper Body Lift",
    body:
      "Addresses skin excess on the upper back, flanks above the waist, and sometimes the arms and lateral chest alongside breast procedures. Performed as a separate stage from the lower body lift. Appropriate for patients with extensive post-weight loss skin excess that is not confined to the lower trunk.",
  },
  {
    title: "Full Body Lift",
    body:
      "The combination of lower and upper body lift, addressing the entire trunk circumferentially. Almost always staged across two separate operations with an appropriate recovery interval between them. Planning the sequence of stages is based on which areas cause the most functional concern and what the patient's health supports in each session.",
  },
  {
    title: "Staged Approach",
    body:
      "For patients who need extensive correction across multiple areas, Dr. Jain stages the procedures into separate operations performed months apart. Staging reduces operative time per session, maintains patient safety, allows full recovery between procedures, and permits assessment of the result from each stage before planning the next. The specific staging plan is individualized to each patient.",
  },
];

function TypesSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Types</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Body Lift Surgery</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PROCEDURE_TYPES.map((item, index) => (
            <div
              key={item.title}
              className="bg-white p-8 border border-[#E8E2D9]"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = [
  {
    label: "Coverage",
    body: "360 degrees around the entire lower trunk, including abdomen, flanks, lower back, buttocks, and outer thighs.",
    tuck: "Front abdomen only.",
    lipo: "Targeted fat areas, no skin removal.",
  },
  {
    label: "Skin Removal",
    body: "Extensive circumferential skin removal.",
    tuck: "Moderate skin removal from the front abdomen.",
    lipo: "None. Skin must contract on its own.",
  },
  {
    label: "Buttock Lift",
    body: "Yes. Buttock elevation is included in the procedure.",
    tuck: "No.",
    lipo: "No.",
  },
  {
    label: "Muscle Repair",
    body: "Yes, when diastasis recti is present alongside skin excess.",
    tuck: "Yes.",
    lipo: "No.",
  },
  {
    label: "Best For",
    body: "Post-massive weight loss patients with circumferential skin excess affecting multiple areas.",
    tuck: "Patients with anterior abdominal skin excess, muscle separation, and localized fat.",
    lipo: "Patients with localized fat deposits and good skin elasticity at or near ideal weight.",
  },
];

function BodyLiftVsRelatedSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="body-lift-vs-related-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Body Lift vs. Tummy Tuck vs. Liposuction
        </h2>
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[880px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-3 pr-2 sm:pr-3 font-serif text-premium-purple font-normal w-[12%]" />
                <th scope="col" className="py-3 px-2 sm:px-2 font-serif text-premium-purple bg-white/80">
                  Body Lift
                </th>
                <th scope="col" className="py-3 px-2 sm:px-2 font-serif text-premium-purple bg-white/80">
                  Tummy Tuck
                </th>
                <th scope="col" className="py-3 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  Liposuction
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-3 pr-2 sm:pr-3 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-3 px-2 sm:px-2 text-deep-navy/70 align-top">{row.body}</td>
                  <td className="py-3 px-2 sm:px-2 text-deep-navy/70 align-top">{row.tuck}</td>
                  <td className="py-3 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.lipo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-6xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed text-sm md:text-base">
          These procedures exist on a spectrum of extent. A tummy tuck is appropriate when skin excess is confined to the front
          abdomen. When skin excess extends to the flanks, lower back, and buttocks simultaneously, a body lift is the procedure that
          addresses the complete problem. Using a tummy tuck on a patient who needs a body lift leaves the circumferential excess
          untreated.
        </p>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Body Lift Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 4 to 8 hours under general anesthesia. Patients are positioned on the operating table to allow access to
              both the front and back of the trunk during the procedure. Dr. Jain marks the planned incision and the extent of skin to be
              removed before surgery with the patient standing, ensuring the incision sits within the underwear line at every point
              around the circumference.
            </p>
            <p>
              The circumferential incision is made around the waist at the planned level. The skin and underlying fat are elevated from
              the muscle fascia. Liposuction is used in areas where fat removal improves the contour before skin excision. The abdominal
              muscles are repaired if diastasis recti is present. The excess skin and fat are excised, the remaining tissue is lifted
              upward, and the buttock component is anchored to the deep fascia before skin closure. The excess is removed, the incision is
              closed in multiple layers, and drains are placed to manage fluid accumulation during the first week of healing.
            </p>
            <p>
              Patients stay in hospital for 1 to 3 nights depending on the extent of the procedure and their post-operative course. Early
              walking is encouraged to reduce the risk of deep vein thrombosis. Compression garments are applied to the entire treatment
              area and worn continuously for the first few weeks.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Significant swelling, bruising, and tightness around the entire circumference of the lower trunk. Drains in place. Walking is encouraged from day 2 onward. Hospital stay of 1 to 3 nights typical.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Drains removed. Light daily activity resumes. Most patients are mobile and managing at home. The dramatic reduction in hanging skin is visible even through post-operative swelling.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Return to desk work for most patients. Compression garments continue. The circumferential improvement in body contour is increasingly apparent as swelling resolves.",
      icon: PersonStanding,
    },
    {
      period: "Months 2 to 6",
      description:
        "Full activity including exercise resumes from 6 to 8 weeks. Final body contour is visible at 3 to 6 months as all deep swelling resolves. Scar maturation continues over 12 to 18 months.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">
              Who Is a Good Candidate for Body Lift Surgery?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults who have achieved significant weight loss and maintained a stable weight for at least 3 to 6
              months, have extensive skin excess affecting multiple areas of the lower trunk, and are in good enough medical health to
              safely undergo an extended surgical procedure under general anesthesia. The weight stability requirement is not arbitrary.
              Operating on a patient who is still losing weight produces a result that is then undermined by further fat loss and skin
              change after surgery.
            </p>
            <p>
              Patients who have undergone bariatric surgery should be at their expected final weight and nutritionally stable before
              body lift. Bariatric surgery creates a period of rapid weight loss followed by stabilization. The surgical planning for body
              lift should happen after this stabilization, not during it. Non-smokers or patients who have stopped smoking at least 4 to
              6 weeks before surgery have significantly better wound healing outcomes. Body lift creates a large area of incision under
              moderate tension, making smoking cessation the single most modifiable factor affecting wound healing complication risk.
              Patients who are medically unfit for a long anesthetic, have uncontrolled chronic conditions, or have unrealistic
              expectations about how much a single operation can address are not appropriate candidates for full body lift in a single
              stage.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Body Lift" />;

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BODY_LIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`body-lift-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Body Lift Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the extent and distribution of your skin excess, your current weight stability,
          and your medical fitness for surgery to determine whether a single-stage or staged approach is appropriate and which areas to
          prioritize. You leave with a realistic understanding of what each stage involves, what the results look like at each step, and
          what the complete transformation requires.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Tummy Tuck",
      description:
        "For patients whose skin excess is confined to the front abdomen without the circumferential involvement that requires a full body lift.",
      href: "/tummy-tuck-surgery-mumbai",
    },
    {
      name: "Arm Lift",
      description: "Address upper arm skin excess as part of a complete post-weight loss body contouring plan.",
      href: "/arm-lift-surgery-mumbai",
    },
    {
      name: "Thigh Lift",
      description: "Inner and outer thigh skin removal to complement lower body lift contouring.",
      href: "/thigh-lift-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BodyLiftPage() {
  return (
    <>
      <SEO
        title="Body Lift Surgery | Post-Weight Loss Contouring by Dr. Vicky Jain"
        description="Lost weight but loose skin remains. Dr. Vicky Jain performs lower body lift surgery for post-bariatric and post-weight loss patients. Book today."
        canonical="/body-lift-surgery-mumbai"
      />
      <BodyLiftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <TypesSection />
      <BodyLiftVsRelatedSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
